import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSettings, usePatchSettings } from "../../api/hooks.js";
import type { UpdateSettingsBody } from "../../api/types.js";
import {
  Button,
  Card,
  CenteredSpinner,
  ErrorState,
  PageHeader,
} from "../../components/ui.js";
import { toast } from "../../components/toast.js";
import { CdnHintPanel } from "./CdnHintPanel.js";
import { SiteConfigFields } from "./SiteConfigFields.js";
import { siteConfigSchema, type SiteConfigForm } from "./siteConfigForm.js";

function EnvironmentCard() {
  const env = import.meta.env as Record<string, string | boolean>;
  const rows = Object.keys(env)
    .filter((k) => k.startsWith("VITE_") || k === "MODE" || k === "DEV")
    .sort();
  return (
    <Card title="Environment">
      <dl className="grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
        {rows.map((k) => (
          <div
            key={k}
            className="flex justify-between gap-2 border-b border-white/[0.04] py-1 last:border-0"
          >
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="truncate font-mono text-foreground/80">
              {String(env[k])}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-xs text-muted-foreground">
        Read-only echo of effective build-time environment.
      </p>
    </Card>
  );
}

export function SettingsPage() {
  const settings = useSettings();
  const patch = usePatchSettings();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SiteConfigForm>({
    resolver: zodResolver(siteConfigSchema),
    defaultValues: {
      siteName: "",
      streamEndGraceMinutes: 5,
      recordingRetentionDays: 30,
      s3Endpoint: "",
      s3Region: "",
      s3Bucket: "",
      s3PublicAssetBase: "",
      s3AccessKey: "",
      s3SecretKey: "",
    },
  });

  // Hydrate form once settings load.
  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    reset({
      siteName: s.siteName,
      streamEndGraceMinutes: s.streamEndGraceMinutes,
      recordingRetentionDays: s.recordingRetentionDays,
      s3Endpoint: s.s3.endpoint ?? "",
      s3Region: s.s3.region ?? "",
      s3Bucket: s.s3.bucket ?? "",
      s3PublicAssetBase: s.s3.publicAssetBase ?? "",
      s3AccessKey: "",
      s3SecretKey: "",
    });
  }, [settings.data, reset]);

  async function onSubmit(values: SiteConfigForm) {
    const body: UpdateSettingsBody = {
      siteName: values.siteName,
      streamEndGraceMinutes: values.streamEndGraceMinutes,
      recordingRetentionDays: values.recordingRetentionDays,
      s3: {
        endpoint: values.s3Endpoint || null,
        region: values.s3Region || null,
        bucket: values.s3Bucket || null,
        publicAssetBase: values.s3PublicAssetBase || null,
        // Only send secrets if the operator actually typed them.
        ...(values.s3AccessKey ? { accessKey: values.s3AccessKey } : {}),
        ...(values.s3SecretKey ? { secretKey: values.s3SecretKey } : {}),
      },
    };
    try {
      await patch.mutateAsync(body);
      toast("Settings saved", "success");
    } catch {
      /* toasted globally */
    }
  }

  return (
    <div className="max-w-3xl space-y-6 p-6 lg:p-8">
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        subtitle="Site configuration, storage and deployment guidance."
      />

      {settings.isLoading && <CenteredSpinner label="Loading settings…" />}
      {settings.error && <ErrorState message={settings.error.message} />}

      {settings.data && (
        <Card title="Site configuration">
          <form onSubmit={handleSubmit(onSubmit)}>
            <SiteConfigFields
              register={register}
              errors={errors}
              s3Configured={settings.data.s3.configured}
            />
            <div className="mt-6 flex justify-end">
              <Button
                type="submit"
                variant="primary"
                loading={patch.isPending}
                disabled={!isDirty && !patch.isPending}
              >
                Save changes
              </Button>
            </div>
          </form>
        </Card>
      )}

      <CdnHintPanel />
      <EnvironmentCard />
    </div>
  );
}
